import { supabase } from './supabaseClient.js';

// Classe BaseEntity para operações CRUD básicas
class BaseEntity {
    constructor(tableName) {
        this.tableName = tableName;
    }

    async create(data) {
        const { data: result, error } = await supabase
            .from(this.tableName)
            .insert(data)
            .select()
            .single();
        
        if (error) throw error;
        return result;
    }

    async list(filters = {}) {
        let query = supabase.from(this.tableName).select('*');
        
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                query = query.eq(key, value);
            }
        });
        
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    }

    async findById(id) {
        const { data, error } = await supabase
            .from(this.tableName)
            .select('*')
            .eq('id', id)
            .single();
        
        if (error) throw error;
        return data;
    }

    async update(id, data) {
        const { data: result, error } = await supabase
            .from(this.tableName)
            .update(data)
            .eq('id', id)
            .select()
            .single();
        
        if (error) throw error;
        return result;
    }

    async delete(id) {
        const { error } = await supabase
            .from(this.tableName)
            .delete()
            .eq('id', id);
        
        if (error) throw error;
        return { success: true };
    }
}

// Função para criar métodos base de entidade
function createBaseEntityMethods(tableName) {
    return {
        async create(data) {
            const { data: result, error } = await supabase
                .from(tableName)
                .insert(data)
                .select()
                .single();
            
            if (error) throw error;
            return result;
        },

        async list(filters = {}) {
            let query = supabase.from(tableName).select('*');
            
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    query = query.eq(key, value);
                }
            });
            
            const { data, error } = await query;
            if (error) throw error;
            return data || [];
        },

        async findById(id) {
            const { data, error } = await supabase
                .from(tableName)
                .select('*')
                .eq('id', id)
                .single();
            
            if (error) throw error;
            return data;
        },

        async update(id, data) {
            const { data: result, error } = await supabase
                .from(tableName)
                .update(data)
                .eq('id', id)
                .select()
                .single();
            
            if (error) throw error;
            return result;
        },

        async delete(id) {
            const { error } = await supabase
                .from(tableName)
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            return { success: true };
        }
    };
}

// Objeto CompanySystem com funções RPC avançadas para relatórios
export const CompanySystem = {
    // Método create usando RPC como primeira opção
    async create(versionData) {
        try {
            console.log('🔄 Tentando criar sistema via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('create_system', {
                version_data: versionData
            });
            
            if (error) {
                console.warn('RPC create_system falhou, tentando inserção direta:', error);
                
                // Fallback: inserção direta na tabela systems
                const { data: directResult, error: directError } = await supabase
                    .from('systems')
                    .insert(versionData)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro na inserção direta:', directError);
                    throw directError;
                }
                
                console.log('✅ Sistema criado via inserção direta:', directResult);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Sistema criado via RPC:', data);
            return data;
        } catch (error) {
            console.error('Error creating system:', error);
            throw error;
        }
    },

    // Método update usando RPC como primeira opção
    async update(versionId, versionData) {
        try {
            console.log('🔄 Tentando atualizar sistema via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('update_system', {
                p_version_id: versionId,
                version_data: versionData
            });
            
            if (error) {
                console.warn('RPC update_system falhou, tentando update direto:', error);
                
                // Fallback: update direto na tabela systems
                const { data: directResult, error: directError } = await supabase
                    .from('systems')
                    .update(versionData)
                    .eq('id', versionId)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro no update direto:', directError);
                    throw directError;
                }
                
                console.log('✅ Sistema atualizado via update direto:', directResult);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Sistema atualizado via RPC:', data);
            return data;
        } catch (error) {
            console.error('Error updating system:', error);
            throw error;
        }
    },

    // Método findById usando RPC como primeira opção
    async findById(versionId) {
        try {
            console.log('🔍 Buscando sistema por ID:', versionId);
            
            // Primeiro, tentar usar função RPC
            try {
                console.log('🔄 Tentando buscar sistema via RPC...');
                const { data, error } = await supabase.rpc('get_system_by_id', {
                    p_version_id: versionId
                });
                
                if (!error && data) {
                    console.log('✅ Sistema encontrado via RPC:', data);
                    return data;
                }
                
                console.warn('RPC get_system_by_id falhou, tentando consulta direta:', error);
            } catch (rpcError) {
                console.warn('RPC get_system_by_id não disponível, tentando consulta direta:', rpcError);
            }
            
            // Fallback: consulta direta
            const { data: directResult, error: directError } = await supabase
                .from('systems')
                .select('*')
                .eq('id', versionId)
                .single();
            
            if (directError) {
                if (directError.code === 'PGRST116') {
                    console.warn(`Sistema com id ${versionId} não encontrado`);
                    return null;
                }
                console.error('❌ Erro na consulta direta:', directError);
                throw directError;
            }
            
            console.log('✅ Sistema encontrado via consulta direta:', directResult);
            return directResult;
        } catch (error) {
            console.error('Error finding system by ID:', error);
            throw error;
        }
    },

    // Método list usando RPC como primeira opção
    async list(filters = {}) {
        try {
            console.log('🔄 Tentando listar sistemas via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('list_systems');
            
            if (error) {
                console.warn('RPC list_systems falhou, tentando consulta direta:', error);
                
                // Fallback: consulta direta
                let query = supabase.from('systems').select('*');
                
                // Aplicar filtros se fornecidos
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        query = query.eq(key, value);
                    }
                });
                
                const { data: directResult, error: directError } = await query;
                
                if (directError) {
                    console.error('❌ Erro na consulta direta:', directError);
                    throw directError;
                }
                
                console.log('✅ Sistemas listados via consulta direta:', directResult?.length || 0);
                return directResult || [];
            }
            
            console.log('✅ Sistemas listados via RPC:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('Error listing systems:', error);
            throw error;
        }
    },

    // Alias para compatibilidade
    async findAll(filters = {}, options = {}) {
        return this.list(filters);
    },

    // Método delete usando consulta direta
    async delete(systemId) {
        try {
            const { error } = await supabase
                .from('systems')
                .delete()
                .eq('id', systemId);
            
            if (error) {
                console.error('❌ Erro ao deletar sistema:', error);
                throw error;
            }
            
            console.log('✅ Sistema deletado com sucesso');
            return true;
        } catch (error) {
            console.error('Error deleting system:', error);
            throw error;
        }
    }
};

// Função para gerar número do ticket
function generateTicketNumber() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    
    // Formato: TICKET-YYYYMMDD-HHMMSS-MMM
    return `TICKET-${year}${month}${day}-${hours}${minutes}${seconds}-${milliseconds}`;
}

// Função para sanitizar dados antes da inserção/atualização
function sanitizeDataForDatabase(data, isCreate = false) {
    const sanitized = { ...data };
    
    // Lista de campos UUID conhecidos na tabela tickets
    const uuidFields = [
        'id', 'project_id', 'assigned_to', 'created_by', 'company_id', 
        'client_id', 'system_id', 'module_id', 'version_id'
    ];
    
    // Lista de campos numéricos
    const numericFields = [
        'priority', 'estimated_hours', 'actual_hours'
    ];
    
    // Lista de campos booleanos
    const booleanFields = [
        'is_billable', 'is_internal', 'is_urgent'
    ];
    
    // Lista de campos obrigatórios que não podem ser null (apenas para criação)
    const requiredFields = {
        'ticket_number': () => generateTicketNumber(),
        'status': () => 'open',
        'title': () => 'Sem título',
        'description': () => 'Sem descrição'
    };
    
    // Lista de campos que não existem na tabela tickets (vindos de JOINs/RPCs)
    const fieldsToRemove = [
        'assigned_avatar_url', 'assigned_name', 'assigned_email',
        'creator_name', 'creator_email', 'creator_avatar_url',
        'company_name', 'client_name', 'system_name', 'module_name',
        'project_name', 'version_name'
    ];
    
    // Função para validar UUID
    const isValidUUID = (uuid) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    };
    
    // Remover campos que não existem na tabela
    fieldsToRemove.forEach(field => {
        if (sanitized[field] !== undefined) {
            console.log(`🗑️ Removendo campo inexistente: ${field}`);
            delete sanitized[field];
        }
    });
    
    // Processar cada campo
    Object.keys(sanitized).forEach(key => {
        const value = sanitized[key];
        
        // Tratar campos UUID
        if (uuidFields.includes(key)) {
            if (value === '' || value === 'undefined' || value === 'null') {
                sanitized[key] = null;
            } else if (value && typeof value === 'string' && !isValidUUID(value)) {
                console.warn(`⚠️ UUID inválido para campo ${key}: ${value}, convertendo para null`);
                sanitized[key] = null;
            }
        }
        
        // Tratar campos numéricos
        else if (numericFields.includes(key)) {
            if (value === '' || value === 'undefined' || value === 'null' || isNaN(value)) {
                sanitized[key] = null;
            } else if (value !== null && value !== undefined) {
                const numValue = Number(value);
                sanitized[key] = isNaN(numValue) ? null : numValue;
            }
        }
        
        // Tratar campos booleanos
        else if (booleanFields.includes(key)) {
            if (value === '' || value === 'undefined' || value === 'null') {
                sanitized[key] = null;
            } else if (typeof value === 'string') {
                sanitized[key] = value.toLowerCase() === 'true';
            }
        }
        
        // Tratar strings vazias em geral
        else if (typeof value === 'string' && value.trim() === '') {
            sanitized[key] = null;
        }
        
        // Remover campos undefined
        else if (value === undefined) {
            delete sanitized[key];
        }
    });
    
    // Garantir campos obrigatórios apenas para criação
    if (isCreate) {
        Object.keys(requiredFields).forEach(field => {
            if (!sanitized[field] || sanitized[field] === null || sanitized[field] === '') {
                sanitized[field] = requiredFields[field]();
                console.log(`✅ Campo obrigatório ${field} definido automaticamente: ${sanitized[field]}`);
            }
        });
    } else {
        // Para updates, apenas remover campos undefined sem aplicar defaults
        Object.keys(sanitized).forEach(field => {
            if (sanitized[field] === undefined) {
                delete sanitized[field];
            }
        });
    }
    
    return sanitized;
}

// Entidade Ticket com mapeamento customizado
export const Ticket = {
    // Método create customizado para mapear campos corretamente
    async create(ticketData) {
        try {
            console.log('🎫 Criando ticket via inserção direta...');
            console.log('📋 Dados recebidos:', ticketData);
            
            // Debug completo da autenticação
            console.log('🔍 === DEBUG AUTENTICAÇÃO ===');
            
            // Verificar se o usuário está autenticado
            const { data: { user }, error: authError } = await supabase.auth.getUser();
            console.log('👤 Usuário Supabase Auth:', user);
            console.log('🔐 Erro de autenticação Supabase:', authError);
            
            // Verificar também a sessão customizada
            const customSession = localStorage.getItem('custom_auth_session');
            console.log('🔑 Sessão customizada (raw):', customSession);
            
            let sessionData = null;
            if (customSession) {
                try {
                    sessionData = JSON.parse(customSession);
                    console.log('🔄 Sessão customizada (parsed):', sessionData);
                } catch (parseError) {
                    console.error('❌ Erro ao parsear sessão customizada:', parseError);
                }
            }
            
            // Verificar User.getCurrentUser()
            try {
                const currentUser = await User.getCurrentUser();
                console.log('👤 User.getCurrentUser():', currentUser);
            } catch (getCurrentUserError) {
                console.error('❌ Erro em User.getCurrentUser():', getCurrentUserError);
            }
            
            // Verificar User.getCurrentProfile()
            try {
                const currentProfile = await User.getCurrentProfile();
                console.log('👤 User.getCurrentProfile():', currentProfile);
            } catch (getCurrentProfileError) {
                console.error('❌ Erro em User.getCurrentProfile():', getCurrentProfileError);
            }
            
            console.log('🔍 === FIM DEBUG AUTENTICAÇÃO ===');
            
            if (!user && (!sessionData || !sessionData.user)) {
                throw new Error('Usuário não autenticado - nenhuma sessão válida encontrada');
            }
            
            // Determinar o usuário a ser usado
            const userToUse = user || (sessionData ? sessionData.user : null);
            console.log('👤 Usuário a ser usado:', userToUse);
            
            if (!userToUse) {
                throw new Error('Usuário não autenticado - não foi possível determinar o usuário');
            }
            
            // Mapear campos do formulário para campos da tabela
            const mappedData = {
                ...ticketData,
                // Gerar número do ticket automaticamente se não fornecido
                ticket_number: ticketData.ticket_number || generateTicketNumber(),
                // Mapear category -> ticket_category
                ticket_category: ticketData.category || null,
                // Mapear subcategory -> ticket_subcategory  
                ticket_subcategory: ticketData.subcategory || null,
                // Garantir que created_by seja definido usando o userToUse
                created_by: ticketData.created_by || ticketData.requester_id || userToUse.id,
                // Adicionar timestamps
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                // Definir status padrão se não fornecido
                status: ticketData.status || 'open',
                // Remover campos originais para evitar conflito
                category: undefined,
                subcategory: undefined,
                requester_id: undefined
            };
            
            console.log('📝 Dados mapeados (antes da sanitização):', mappedData);
            
            // Sanitizar dados para evitar erros de tipo no PostgreSQL
            const sanitizedData = sanitizeDataForDatabase(mappedData, true);
            
            console.log('🧹 Dados sanitizados para inserção:', sanitizedData);
            
            // Usar inserção direta na tabela tickets
            const { data, error } = await supabase
                .from('tickets')
                .insert(sanitizedData)
                .select()
                .single();
            
            console.log('📡 Resposta da inserção:', { data, error });
            
            if (error) {
                console.error('❌ Erro na inserção do ticket:', error);
                throw error;
            }
            
            console.log('✅ Ticket criado com sucesso via inserção direta:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao criar ticket:', error);
            console.error('📊 Stack trace:', error.stack);
            throw error;
        }
    },

    // Método update customizado para mapear campos corretamente
    async update(ticketId, updateData) {
        try {
            console.log('✏️ Atualizando ticket com mapeamento de campos...');
            
            // Mapear campos do formulário para campos da tabela
            const mappedData = {
                ...updateData,
                // Mapear category -> ticket_category se presente
                ...(updateData.category !== undefined && { ticket_category: updateData.category }),
                // Mapear subcategory -> ticket_subcategory se presente
                ...(updateData.subcategory !== undefined && { ticket_subcategory: updateData.subcategory }),
                // Remover campos originais para evitar conflito
                category: undefined,
                subcategory: undefined,
                updated_at: new Date().toISOString()
            };
            
            console.log('📝 Dados mapeados (antes da sanitização):', mappedData);
            
            // Sanitizar dados para evitar erros de tipo no PostgreSQL
            const sanitizedData = sanitizeDataForDatabase(mappedData, false);
            
            console.log('🧹 Dados sanitizados para atualização:', sanitizedData);
            
            const { data, error } = await supabase
                .from('tickets')
                .update(sanitizedData)
                .eq('id', ticketId)
                .select()
                .single();
            
            if (error) throw error;
            console.log('✅ Ticket atualizado com sucesso:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao atualizar ticket:', error);
            throw error;
        }
    },

    // Método delete
    async delete(id) {
        const { error } = await supabase
            .from('tickets')
            .delete()
            .eq('id', id);
        
        if (error) throw error;
        return { success: true };
    }
};

// Sobrescrever o método list para usar RPC
Ticket.list = async function(filters = {}) {
    try {
        console.log('🔄 Listando tickets via RPC list_tickets...');
        
        // Usar a função RPC que tem SECURITY DEFINER
        const { data, error } = await supabase.rpc('list_tickets');
        
        if (error) {
            console.error('❌ Erro na RPC list_tickets:', error);
            throw error;
        }
        
        let tickets = data || [];
        console.log('✅ Tickets obtidos via RPC:', tickets.length);
        
        // Aplicar filtros localmente se fornecidos
        if (Object.keys(filters).length > 0) {
            tickets = tickets.filter(ticket => {
                return Object.entries(filters).every(([key, value]) => {
                    if (value === undefined || value === null || value === '') {
                        return true;
                    }
                    return ticket[key] === value;
                });
            });
            console.log('✅ Tickets após filtros:', tickets.length);
        }
        
        return tickets;
    } catch (error) {
        console.error('❌ Erro ao listar tickets:', error);
        throw error;
    }
};

// Sobrescrever o método findById para usar RPC
Ticket.findById = async function(ticketId) {
    try {
        console.log('🔍 Buscando ticket por ID via RPC get_ticket_by_id:', ticketId);
        
        // Usar a função RPC que tem SECURITY DEFINER
        const { data, error } = await supabase.rpc('get_ticket_by_id', {
            p_ticket_id: ticketId
        });
        
        if (error) {
            console.error('❌ Erro na RPC get_ticket_by_id:', error);
            throw error;
        }
        
        if (!data) {
            console.warn(`⚠️ Ticket com ID ${ticketId} não encontrado`);
            return null;
        }
        
        console.log('✅ Ticket encontrado via RPC:', data);
        return data;
    } catch (error) {
        console.error('❌ Erro ao buscar ticket por ID:', error);
        throw error;
    }
};
export const TicketComment = {
    ...createBaseEntityMethods('ticket_comments'),
    
    // Método list usando RPC list_ticket_comments para incluir dados do usuário
    async list(filters = {}) {
        try {
            console.log('🔄 Listando comentários via RPC list_ticket_comments...');
            
            const { data, error } = await supabase.rpc('list_ticket_comments', {
                filter_data: filters
            });
            
            if (error) {
                console.error('❌ Erro na RPC list_ticket_comments:', error);
                throw error;
            }
            
            console.log('✅ Comentários listados via RPC:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('❌ Erro ao listar comentários:', error);
            throw error;
        }
    }
};
export const Project = {
    ...createBaseEntityMethods('projects'),
    
    // Método findById usando RPC get_project_by_id
    async findById(projectId) {
        try {
            console.log('🔍 Buscando projeto por ID via RPC get_project_by_id:', projectId);
            
            const { data, error } = await supabase.rpc('get_project_by_id', {
                p_project_id: projectId
            });
            
            if (error) {
                console.error('❌ Erro na RPC get_project_by_id:', error);
                throw error;
            }
            
            if (!data) {
                console.warn(`⚠️ Projeto com ID ${projectId} não encontrado`);
                return null;
            }
            
            console.log('✅ Projeto encontrado via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar projeto por ID:', error);
            throw error;
        }
    },
    
    // Alias para compatibilidade
    async findAll(filters = {}, options = {}) {
        return this.list(filters);
    }
};
export const ProjectTask = new BaseEntity('project_tasks');
export const KnowledgeBase = new BaseEntity('knowledge_base');
export const Company = {
    ...createBaseEntityMethods('companies'),
    
    // Método findById usando RPC get_company_by_id
    async findById(companyId) {
        try {
            console.log('🔍 Buscando empresa por ID via RPC get_company_by_id:', companyId);
            
            const { data, error } = await supabase.rpc('get_company_by_id', {
                p_company_id: companyId
            });
            
            if (error) {
                console.error('❌ Erro na RPC get_company_by_id:', error);
                throw error;
            }
            
            if (!data) {
                console.warn(`⚠️ Empresa com ID ${companyId} não encontrada`);
                return null;
            }
            
            console.log('✅ Empresa encontrada via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar empresa por ID:', error);
            throw error;
        }
    },
    
    // Alias para compatibilidade
    async findAll(filters = {}, options = {}) {
        return this.list(filters);
    }
};
export const Invoice = new BaseEntity('invoices');
export const Payment = new BaseEntity('payments');
export const AuditLog = new BaseEntity('audit_logs');
export const System = {
    ...createBaseEntityMethods('systems'),
    
    // Método create usando RPC function com SECURITY DEFINER
    async create(systemData) {
        try {
            console.log('🔄 Criando sistema via RPC...');
            
            // Verificar se há usuário autenticado
            const currentUser = await User.getCurrentUser();
            if (!currentUser) {
                throw new Error('Usuário não autenticado. Faça login para criar sistemas.');
            }
            
            console.log('👤 Usuário autenticado:', currentUser.email || currentUser.id);
            
            // Preparar dados para a função RPC
            const dataForRPC = {
                ...systemData,
                created_by: currentUser.id,
                status: systemData.status || 'active'
            };
            
            // Converter campos de data vazios para null
            if (dataForRPC.installation_date === '') {
                dataForRPC.installation_date = null;
            }
            if (dataForRPC.expiration_date === '') {
                dataForRPC.expiration_date = null;
            }
            
            console.log('📝 Dados para RPC create_system:', dataForRPC);
            
            // Usar a função RPC create_system
            const { data, error } = await supabase.rpc('create_system', {
                system_data: dataForRPC
            });
            
            if (error) {
                console.error('❌ Erro na função RPC create_system:', error);
                throw error;
            }
            
            console.log('✅ Sistema criado via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao criar sistema:', error);
            throw error;
        }
    },

    // Método update usando RPC function com SECURITY DEFINER
    async update(systemId, systemData) {
        try {
            console.log('🔄 Atualizando sistema via RPC...');
            
            // Verificar se há usuário autenticado
            const currentUser = await User.getCurrentUser();
            if (!currentUser) {
                throw new Error('Usuário não autenticado. Faça login para atualizar sistemas.');
            }
            
            console.log('👤 Usuário autenticado:', currentUser.email || currentUser.id);
            
            // Preparar dados para atualização
            const dataForRPC = {
                ...systemData
            };
            
            // Converter campos de data vazios para null
            if (dataForRPC.installation_date === '') {
                dataForRPC.installation_date = null;
            }
            if (dataForRPC.expiration_date === '') {
                dataForRPC.expiration_date = null;
            }
            
            console.log('📝 Dados para RPC update_system:', dataForRPC);
            
            // Usar a função RPC update_system
            const { data, error } = await supabase.rpc('update_system', {
                p_system_id: systemId,
                system_data: dataForRPC
            });
            
            if (error) {
                console.error('❌ Erro na função RPC update_system:', error);
                throw error;
            }
            
            console.log('✅ Sistema atualizado via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao atualizar sistema:', error);
            throw error;
        }
    },
    
    // Método list usando função RPC com fallback para query direta
    async list() {
        try {
            console.log('🔄 Listando sistemas via RPC...');
            
            // Tentar usar a função RPC primeiro
            const { data: rpcData, error: rpcError } = await supabase.rpc('list_systems');
            
            if (!rpcError && rpcData) {
                console.log('✅ Sistemas listados via RPC:', rpcData.length, 'registros');
                return rpcData;
            }
            
            console.log('⚠️ RPC falhou, usando query direta:', rpcError?.message);
            
            // Fallback para query direta
            const { data, error } = await supabase
                .from('systems')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) {
                console.error('❌ Erro na query direta:', error);
                throw error;
            }
            
            console.log('✅ Sistemas listados via query direta:', data.length, 'registros');
            return data;
        } catch (error) {
            console.error('❌ Erro ao listar sistemas:', error);
            throw error;
        }
    },
    
    // Método get usando função RPC com fallback para query direta
    async get(systemId) {
        try {
            console.log('🔄 Buscando sistema via RPC...');
            
            // Tentar usar a função RPC primeiro
            const { data: rpcData, error: rpcError } = await supabase.rpc('get_system_by_id', {
                p_system_id: systemId
            });
            
            if (!rpcError && rpcData) {
                console.log('✅ Sistema encontrado via RPC:', rpcData);
                return rpcData;
            }
            
            console.log('⚠️ RPC falhou, usando query direta:', rpcError?.message);
            
            // Fallback para query direta
            const { data, error } = await supabase
                .from('systems')
                .select('*')
                .eq('id', systemId)
                .single();
            
            if (error) {
                console.error('❌ Erro na query direta:', error);
                throw error;
            }
            
            console.log('✅ Sistema encontrado via query direta:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar sistema:', error);
            throw error;
        }
    },
    
    // Método delete usando função RPC com fallback para query direta
    async delete(systemId) {
        try {
            console.log('🔄 Deletando sistema via RPC...');
            
            // Verificar se há usuário autenticado
            const currentUser = await User.getCurrentUser();
            if (!currentUser) {
                throw new Error('Usuário não autenticado. Faça login para deletar sistemas.');
            }
            
            // Tentar usar a função RPC primeiro
            const { data: rpcData, error: rpcError } = await supabase.rpc('delete_system', {
                p_system_id: systemId
            });
            
            if (!rpcError) {
                console.log('✅ Sistema deletado via RPC:', rpcData);
                return rpcData;
            }
            
            console.log('⚠️ RPC falhou, usando query direta:', rpcError?.message);
            
            // Fallback para query direta
            const { data, error } = await supabase
                .from('systems')
                .delete()
                .eq('id', systemId)
                .select()
                .single();
            
            if (error) {
                console.error('❌ Erro na query direta:', error);
                throw error;
            }
            
            console.log('✅ Sistema deletado via query direta:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao deletar sistema:', error);
            throw error;
        }
    },
    
    // Alias para compatibilidade
    async findAll(filters = {}, options = {}) {
        return this.list(filters);
    }
};

// Objeto SystemVersion personalizado com suporte a RPC
export const SystemVersion = {
    // Método create usando RPC como primeira opção
    async create(versionData) {
        try {
            console.log('🔄 Tentando criar versão via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('create_system_version', {
                version_data: versionData
            });
            
            if (error) {
                console.warn('RPC create_system_version falhou, tentando inserção direta:', error);
                
                // Fallback: inserção direta na tabela system_versions
                const { data: directResult, error: directError } = await supabase
                    .from('system_versions')
                    .insert(versionData)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro na inserção direta:', directError);
                    throw directError;
                }
                
                console.log('✅ Versão criada via inserção direta:', directResult);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Versão criada via RPC:', data);
            return data;
        } catch (error) {
            console.error('Error creating system version:', error);
            throw error;
        }
    },

    // Método update usando RPC como primeira opção
    async update(versionId, versionData) {
        try {
            console.log('🔄 Tentando atualizar versão via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('update_system_version', {
                p_version_id: versionId,
                version_data: versionData
            });
            
            if (error) {
                console.warn('RPC update_system_version falhou, tentando update direto:', error);
                
                // Fallback: update direto na tabela system_versions
                const { data: directResult, error: directError } = await supabase
                    .from('system_versions')
                    .update(versionData)
                    .eq('id', versionId)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro no update direto:', directError);
                    throw directError;
                }
                
                console.log('✅ Versão atualizada via update direto:', directResult);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Versão atualizada via RPC:', data);
            return data;
        } catch (error) {
            console.error('Error updating system version:', error);
            throw error;
        }
    },

    // Método findById usando RPC como primeira opção
    async findById(versionId) {
        try {
            console.log('🔍 Buscando versão por ID:', versionId);
            
            // Primeiro, tentar usar função RPC
            try {
                console.log('🔄 Tentando buscar versão via RPC...');
                const { data, error } = await supabase.rpc('get_system_version_by_id', {
                    p_version_id: versionId
                });
                
                if (!error && data) {
                    console.log('✅ Versão encontrada via RPC:', data);
                    return data;
                }
                
                console.warn('RPC get_system_version_by_id falhou, tentando consulta direta:', error);
            } catch (rpcError) {
                console.warn('RPC get_system_version_by_id não disponível, tentando consulta direta:', rpcError);
            }
            
            // Fallback: consulta direta
            const { data: directResult, error: directError } = await supabase
                .from('system_versions')
                .select('*')
                .eq('id', versionId)
                .single();
            
            if (directError) {
                if (directError.code === 'PGRST116') {
                    console.warn(`Versão com id ${versionId} não encontrada`);
                    return null;
                }
                console.error('❌ Erro na consulta direta:', directError);
                throw directError;
            }
            
            console.log('✅ Versão encontrada via consulta direta:', directResult);
            return directResult;
        } catch (error) {
            console.error('Error finding system version by ID:', error);
            throw error;
        }
    },

    // Método list usando RPC como primeira opção
    async list(filters = {}) {
        try {
            console.log('🔄 Tentando listar versões via RPC...');
            
            // Primeiro, tentar usar função RPC
            const { data, error } = await supabase.rpc('list_system_versions');
            
            if (error) {
                console.warn('RPC list_system_versions falhou, tentando consulta direta:', error);
                
                // Fallback: consulta direta
                let query = supabase.from('system_versions').select('*');
                
                // Aplicar filtros se fornecidos
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        query = query.eq(key, value);
                    }
                });
                
                const { data: directResult, error: directError } = await query;
                
                if (directError) {
                    console.error('❌ Erro na consulta direta:', directError);
                    throw directError;
                }
                
                console.log('✅ Versões listadas via consulta direta:', directResult?.length || 0);
                return directResult || [];
            }
            
            console.log('✅ Versões listadas via RPC:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('Error listing system versions:', error);
            throw error;
        }
    },

    // Alias para compatibilidade
    async findAll(filters = {}, options = {}) {
        return this.list(filters);
    },

    // Método delete usando consulta direta
    async delete(versionId) {
        try {
            const { error } = await supabase
                .from('system_versions')
                .delete()
                .eq('id', versionId);
            
            if (error) {
                console.error('❌ Erro ao deletar versão:', error);
                throw error;
            }
            
            console.log('✅ Versão deletada com sucesso');
            return true;
        } catch (error) {
            console.error('Error deleting system version:', error);
            throw error;
        }
    }
};

export const CompanySystemEntity = new BaseEntity('company_systems');
export const UserMessage = new BaseEntity('user_messages');
export const UserFavorite = new BaseEntity('user_favorites');
export const UserNotification = new BaseEntity('user_notifications');
export const UserCalendarEvent = new BaseEntity('user_calendar_events');
export const TimesheetEntry = new BaseEntity('timesheet_entries');

// Objeto ChatMessage personalizado para lidar com RLS
export const ChatMessage = {
    async create(messageData) {
        try {
            console.log('🔍 Tentando criar mensagem de chat:', messageData);
            
            // Mapear dados corretamente para RPC
            const rpcParams = {
                p_company_id: messageData.company_id || null,
                p_room_id: messageData.room_id || messageData.ticket_id || null,
                p_user_id: messageData.user_id || messageData.sender_id || null,
                p_message: messageData.message || messageData.content || '',
                p_message_type: messageData.message_type || messageData.type || 'message',
                p_attachments: messageData.attachments || [],
                p_reply_to: messageData.reply_to || null
            };
            
            console.log('🔍 Parâmetros RPC:', rpcParams);
            
            // Tentar usar função RPC first (mais segura)
            const { data, error: rpcError } = await supabase.rpc('create_chat_message', rpcParams);
            
            if (!rpcError && data) {
                console.log('✅ Mensagem criada via RPC create_chat_message');
                return Array.isArray(data) ? data[0] : data;
            }
            
            console.warn('RPC create_chat_message falhou, tentando inserção direta:', rpcError);
            
            // Fallback: inserção direta com validação de campos
            const validatedData = {
                company_id: messageData.company_id || null,
                room_id: messageData.room_id || messageData.ticket_id || null,
                user_id: messageData.user_id || messageData.sender_id || null,
                message: messageData.message || messageData.content || '',
                message_type: messageData.message_type || messageData.type || 'message',
                attachments: messageData.attachments || [],
                reply_to: messageData.reply_to || null,
                is_edited: false,
                is_deleted: false,
                reactions: []
            };
            
            console.log('🔍 Dados validados para inserção:', validatedData);
            
            const { data: directData, error: directError } = await supabase
                .from('chat_messages')
                .insert(validatedData)
                .select()
                .single();
            
            if (directError) {
                console.error('❌ Erro na inserção direta de chat_messages:', directError);
                console.error('Detalhes do erro:', {
                    code: directError.code,
                    message: directError.message,
                    details: directError.details,
                    hint: directError.hint
                });
                throw directError;
            }
            
            console.log('✅ Mensagem criada via inserção direta');
            return directData;
        } catch (error) {
            console.error('❌ Error creating chat message:', error);
            throw error;
        }
    },

    async list(filters = {}) {
        try {
            // Tentar função RPC first
            const { data, error: rpcError } = await supabase.rpc('list_chat_messages', {
                filter_data: filters
            });
            
            if (!rpcError && data) {
                console.log('✅ Mensagens carregadas via RPC list_chat_messages');
                return Array.isArray(data) ? data : [data];
            }
            
            console.warn('RPC list_chat_messages falhou, tentando consulta direta:', rpcError);
            
            // Fallback: consulta direta
            let query = supabase.from('chat_messages').select('*');
            
            Object.keys(filters).forEach(key => {
                if (filters[key] !== undefined && filters[key] !== null) {
                    query = query.eq(key, filters[key]);
                }
            });
            
            const { data: directData, error: directError } = await query.order('created_at', { ascending: true });
            
            if (directError) {
                console.error('Erro na consulta direta de chat_messages:', directError);
                throw directError;
            }
            
            console.log('✅ Mensagens carregadas via consulta direta');
            return directData || [];
        } catch (error) {
            console.error('Error listing chat messages:', error);
            throw error;
        }
    },

    async findById(messageId) {
        try {
            // Tentar função RPC first
            const { data, error: rpcError } = await supabase.rpc('get_chat_message_by_id', {
                message_id: messageId
            });
            
            if (!rpcError && data) {
                console.log('✅ Mensagem encontrada via RPC get_chat_message_by_id');
                return data;
            }
            
            console.warn('RPC get_chat_message_by_id falhou, tentando consulta direta:', rpcError);
            
            // Fallback: consulta direta
            const { data: directData, error: directError } = await supabase
                .from('chat_messages')
                .select('*')
                .eq('id', messageId)
                .single();
            
            if (directError) {
                console.error('Erro na consulta direta de chat_message:', directError);
                throw directError;
            }
            
            console.log('✅ Mensagem encontrada via consulta direta');
            return directData;
        } catch (error) {
            console.error('Error finding chat message:', error);
            throw error;
        }
    },

    async update(messageId, messageData) {
        try {
            // Tentar função RPC first
            const { data, error: rpcError } = await supabase.rpc('update_chat_message', {
                message_id: messageId,
                message_data: messageData
            });
            
            if (!rpcError && data) {
                console.log('✅ Mensagem atualizada via RPC update_chat_message');
                return data;
            }
            
            console.warn('RPC update_chat_message falhou, tentando atualização direta:', rpcError);
            
            // Fallback: atualização direta
            const { data: directData, error: directError } = await supabase
                .from('chat_messages')
                .update(messageData)
                .eq('id', messageId)
                .select()
                .single();
            
            if (directError) {
                console.error('Erro na atualização direta de chat_message:', directError);
                throw directError;
            }
            
            console.log('✅ Mensagem atualizada via atualização direta');
            return directData;
        } catch (error) {
            console.error('Error updating chat message:', error);
            throw error;
        }
    },

    async delete(messageId) {
        try {
            // Tentar função RPC first
            const { data, error: rpcError } = await supabase.rpc('delete_chat_message', {
                message_id: messageId
            });
            
            if (!rpcError) {
                console.log('✅ Mensagem deletada via RPC delete_chat_message');
                return data;
            }
            
            console.warn('RPC delete_chat_message falhou, tentando deleção direta:', rpcError);
            
            // Fallback: deleção direta
            const { data: directData, error: directError } = await supabase
                .from('chat_messages')
                .delete()
                .eq('id', messageId)
                .select()
                .single();
            
            if (directError) {
                console.error('Erro na deleção direta de chat_message:', directError);
                throw directError;
            }
            
            console.log('✅ Mensagem deletada via deleção direta');
            return directData;
        } catch (error) {
            console.error('Error deleting chat message:', error);
            throw error;
        }
    }
};

// Objeto User personalizado para autenticação
export const User = {
    ...createBaseEntityMethods('user_profiles'),
    
    // Método findById usando RPC get_user_by_id
    async findById(userId) {
        try {
            console.log('🔍 Buscando usuário por ID via RPC get_user_by_id:', userId);
            
            const { data, error } = await supabase.rpc('get_user_by_id', {
                p_user_id: userId
            });
            
            if (error) {
                console.error('❌ Erro na RPC get_user_by_id:', error);
                throw error;
            }
            
            if (!data) {
                console.warn(`⚠️ Usuário com ID ${userId} não encontrado`);
                return null;
            }
            
            console.log('✅ Usuário encontrado via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar usuário por ID:', error);
            throw error;
        }
    },
    
    // Obter usuário atual
    async getCurrentUser() {
        try {
            const { data: { user }, error } = await supabase.auth.getUser();
            if (error || !user) {
                // Tentar obter da sessão customizada
                const customSession = this._getCustomSession();
                return customSession ? customSession.user : null;
            }
            return user;
        } catch (error) {
            // Fallback para sessão customizada
            const customSession = this._getCustomSession();
            return customSession ? customSession.user : null;
        }
    },

    // Obter perfil do usuário atual
    async getCurrentProfile() {
        try {
            // Primeiro, tentar obter usuário da sessão normal do Supabase
            const { data: { user }, error: authError } = await supabase.auth.getUser();
            
            let currentUser = null;
            
            if (authError || !user) {
                // Se não há sessão do Supabase, verificar se há dados de sessão customizada
                const customSession = this._getCustomSession();
                if (customSession && customSession.user) {
                    currentUser = customSession.user;
                } else {
                    return null;
                }
            } else {
                currentUser = user;
            }

            // Tentar usar a função RPC mais segura primeiro
            try {
                const { data: profileData, error: rpcError } = await supabase.rpc('get_user_profile', {
                    user_uuid: currentUser.id
                });
                
                if (!rpcError && profileData && profileData.length > 0) {
                    console.log('✅ Perfil encontrado via RPC (array):', profileData[0]);
                    console.log('🖼️ Avatar URL na resposta RPC (array):', profileData[0].avatar_url);
                    return profileData[0];
                } else if (!rpcError && profileData) {
                    console.log('✅ Perfil encontrado via RPC (objeto único):', profileData);
                    console.log('🖼️ Avatar URL na resposta RPC (objeto):', profileData.avatar_url);
                    console.log('🔍 Tipo de dados profileData:', typeof profileData);
                    console.log('🔍 Keys do profileData:', Object.keys(profileData));
                    return profileData;
                }
                
                console.warn('RPC get_user_profile falhou, tentando consulta direta:', rpcError);
            } catch (rpcError) {
                console.warn('RPC get_user_profile não disponível, tentando consulta direta:', rpcError);
            }

            // Fallback: consulta direta (pode falhar com RLS)
            console.log('🔍 Tentando consulta direta para usuário:', currentUser.id);
            const { data, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', currentUser.id)
                .single();
            
            if (error) {
                console.warn('Consulta direta falhou, usando dados básicos:', error);
                
                // Último fallback: usar dados da sessão customizada se disponível
                const customSession = this._getCustomSession();
                if (customSession && customSession.user) {
                    console.log('✅ Usando dados da sessão customizada (fallback):', customSession.user);
                    return {
                        id: customSession.user.id,
                        email: customSession.user.email,
                        name: customSession.user.name || customSession.user.email,
                        role: customSession.user.role || 'client',
                        company_id: customSession.user.company_id || null,
                        is_admin: customSession.user.role === 'admin',
                        preferences: {}
                    };
                }
                
                // Se não encontrar perfil, retorna dados básicos
                return {
                    id: currentUser.id,
                    email: currentUser.email,
                    name: currentUser.user_metadata?.full_name || currentUser.email,
                    role: currentUser.user_metadata?.role || 'client',
                    company_id: currentUser.user_metadata?.company_id || null,
                    is_admin: currentUser.user_metadata?.is_admin || false,
                    preferences: {}
                };
            }
            
            console.log('✅ Perfil encontrado via consulta direta:', data);
            return data;
        } catch (error) {
            console.error('Error getting current profile:', error);
            
            // Último fallback: tentar usar dados de sessão customizada
            const customSession = this._getCustomSession();
            if (customSession && customSession.user) {
                console.log('✅ Usando dados da sessão customizada:', customSession.user);
                return {
                    id: customSession.user.id,
                    email: customSession.user.email,
                    name: customSession.user.name || customSession.user.email,
                    role: customSession.user.role || 'client',
                    company_id: customSession.user.company_id || null,
                    is_admin: customSession.user.role === 'admin',
                    preferences: {}
                };
            }
            
            throw error;
        }
    },

    // Gerenciar sessão customizada
    _setCustomSession(sessionData) {
        localStorage.setItem('custom_auth_session', JSON.stringify(sessionData));
    },

    _getCustomSession() {
        try {
            const sessionStr = localStorage.getItem('custom_auth_session');
            return sessionStr ? JSON.parse(sessionStr) : null;
        } catch {
            return null;
        }
    },

    _clearCustomSession() {
        localStorage.removeItem('custom_auth_session');
    },

    // Método me() para compatibilidade com o código existente
    async me() {
        try {
            const profile = await this.getCurrentProfile();
            if (!profile) {
                throw new Error('User profile not found');
            }
            return profile;
        } catch (error) {
            console.error('Error in User.me():', error);
            throw error;
        }
    },

    // Método para atualizar dados do usuário atual
    async updateMyUserData(data) {
        try {
            const currentUser = await this.getCurrentUser();
            if (!currentUser) {
                console.warn('No authenticated user for updateMyUserData');
                return null;
            }

            // Tentar atualizar diretamente, tratando erros de forma não-crítica
            const { data: result, error } = await supabase
                .from('user_profiles')
                .update(data)
                .eq('id', currentUser.id)
                .select();

            if (error) {
                // Tratar erros comuns de forma não-crítica
                if (error.code === 'PGRST116' || error.code === '42501' || error.message.includes('permission denied')) {
                    console.warn('Cannot update user preferences - RLS policy or user not found:', error.message);
                    return null;
                }
                
                // Para outros erros, apenas log sem quebrar
                console.warn('Non-critical error updating user data:', error.message);
                return null;
            }
            
            // Verificar se alguma linha foi retornada
            if (!result || result.length === 0) {
                console.warn('Update executed but no data returned');
                return null;
            }

            console.log('✅ User preferences updated successfully');
            return result[0];
        } catch (error) {
            // Capturar qualquer erro não previsto de forma não-crítica
            console.warn('Non-critical error in updateMyUserData:', error.message);
            return null;
        }
    },

    // Método logout para compatibilidade
    async logout() {
        return await this.signOut();
    },

    // Método para buscar usuário por ID
    async findById(userId) {
        try {
            console.log('🔍 Buscando usuário por ID:', userId);
            
            // Primeiro, tentar usar a função RPC mais segura
            try {
                console.log('🔄 Tentando buscar usuário via RPC...');
                const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_by_id', {
                    user_uuid: userId
                });
                
                console.log('🔍 RPC get_user_by_id result:', { rpcData, rpcError });
                console.log('🔍 Tipo rpcData:', typeof rpcData);
                console.log('🔍 Array rpcData?:', Array.isArray(rpcData));
                
                if (!rpcError && rpcData) {
                    if (Array.isArray(rpcData) && rpcData.length > 0) {
                        console.log('✅ Usuário encontrado via RPC (array):', rpcData[0]);
                        return rpcData[0];
                    } else if (!Array.isArray(rpcData) && rpcData.id) {
                        console.log('✅ Usuário encontrado via RPC (objeto único):', rpcData);
                        return rpcData;
                    }
                }
                
                console.warn('RPC get_user_by_id falhou, tentando RPC básica:', rpcError);
            } catch (rpcError) {
                console.warn('RPC get_user_by_id não disponível, tentando RPC básica:', rpcError);
            }
            
            // Segundo fallback: tentar RPC sem JOIN
            try {
                console.log('🔄 Tentando buscar usuário via RPC básica...');
                const { data: basicRpcData, error: basicRpcError } = await supabase.rpc('get_user_basic', {
                    user_uuid: userId
                });
                
                console.log('🔍 RPC get_user_basic result:', { basicRpcData, basicRpcError });
                console.log('🔍 Tipo basicRpcData:', typeof basicRpcData);
                console.log('🔍 Array basicRpcData?:', Array.isArray(basicRpcData));
                
                if (!basicRpcError && basicRpcData) {
                    if (Array.isArray(basicRpcData) && basicRpcData.length > 0) {
                        console.log('✅ Usuário encontrado via RPC básica (array):', basicRpcData[0]);
                        const userData = basicRpcData[0];
                        userData.companies = null; // Não buscar empresa neste caso
                        return userData;
                    } else if (!Array.isArray(basicRpcData) && basicRpcData.id) {
                        console.log('✅ Usuário encontrado via RPC básica (objeto único):', basicRpcData);
                        basicRpcData.companies = null;
                        return basicRpcData;
                    }
                }
                
                console.warn('RPC get_user_basic falhou, tentando consulta direta:', basicRpcError);
            } catch (basicRpcError) {
                console.warn('RPC get_user_basic não disponível, tentando RPC get_user_profile:', basicRpcError);
            }
            
            // Terceiro fallback: tentar RPC get_user_profile
            try {
                console.log('🔄 Tentando buscar usuário via RPC get_user_profile...');
                const { data: profileRpcResult, error: profileRpcError } = await supabase.rpc('get_user_profile', {
                    user_uuid: userId
                });
                
                console.log('📊 Resultado get_user_profile:', { profileRpcResult, profileRpcError });
                console.log('🔍 Tipo profileRpcResult:', typeof profileRpcResult);
                console.log('🔍 Array profileRpcResult?:', Array.isArray(profileRpcResult));
                console.log('🔍 profileRpcResult length:', profileRpcResult?.length);
                console.log('🔍 profileRpcResult[0]:', profileRpcResult?.[0]);
                
                if (!profileRpcError && profileRpcResult) {
                    console.log('🔍 profileRpcResult detalhado:', profileRpcResult);
                    
                    // A RPC pode retornar um array ou um objeto direto
                    let userProfile = null;
                    
                    if (Array.isArray(profileRpcResult) && profileRpcResult.length > 0) {
                        // Caso retorne array com objeto aninhado
                        userProfile = profileRpcResult[0]?.get_user_profile;
                        console.log('🔍 userProfile extraído de array:', userProfile);
                    } else if (!Array.isArray(profileRpcResult) && profileRpcResult.get_user_profile) {
                        // Caso retorne objeto direto com get_user_profile
                        userProfile = profileRpcResult.get_user_profile;
                        console.log('🔍 userProfile extraído de objeto:', userProfile);
                    } else if (!Array.isArray(profileRpcResult) && profileRpcResult.id) {
                        // Caso retorne objeto direto com dados do usuário
                        userProfile = profileRpcResult;
                        console.log('🔍 userProfile é objeto direto:', userProfile);
                    }
                    
                    if (userProfile) {
                        console.log('✅ Usuário encontrado via RPC get_user_profile:', userProfile);
                        return userProfile;
                    } else {
                        console.log('⚠️ userProfile é null/undefined após extração');
                    }
                } else {
                    console.log('⚠️ Condição falhou:', {
                        hasError: !!profileRpcError,
                        hasResult: !!profileRpcResult
                    });
                }
                
                console.warn('RPC get_user_profile falhou, tentando consulta direta:', profileRpcError);
            } catch (profileRpcError) {
                console.warn('RPC get_user_profile não disponível, tentando consulta direta:', profileRpcError);
            }
            
            // Quarto fallback: tentar buscar apenas os dados do usuário
            console.log('🔄 Tentando consulta direta na tabela...');
            const { data: userData, error: userError } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', userId)
                .maybeSingle();
            
            if (userError) {
                console.error('❌ Erro ao buscar dados do usuário:', userError);
                throw userError;
            }
            
            if (!userData) {
                console.warn('⚠️ Usuário não encontrado:', userId);
                return null;
            }
            
            console.log('✅ Dados do usuário encontrados:', userData);
            
            // Se o usuário tem company_id, tentar buscar dados da empresa
            if (userData.company_id) {
                try {
                    console.log('🔍 Buscando dados da empresa:', userData.company_id);
                    const { data: companyData, error: companyError } = await supabase
                        .from('companies')
                        .select('id, name, email, phone')
                        .eq('id', userData.company_id)
                        .single();
                    
                    if (!companyError && companyData) {
                        console.log('✅ Dados da empresa encontrados:', companyData);
                        userData.companies = companyData;
                    } else {
                        console.warn('⚠️ Erro ao buscar empresa ou empresa não encontrada:', companyError);
                        userData.companies = null;
                    }
                } catch (companyError) {
                    console.warn('⚠️ Erro não crítico ao buscar empresa:', companyError);
                    userData.companies = null;
                }
            } else {
                console.log('ℹ️ Usuário não tem empresa associada');
                userData.companies = null;
            }
            
            return userData;
        } catch (error) {
            console.error('Error finding user by ID:', error);
            throw error;
        }
    },

    // Alias para compatibilidade
    async get(userId) {
        return this.findById(userId);
    },

    // Método list para listar usuários (admin)
    async list(filters = {}) {
        try {
            // Primeiro tentar usar a função RPC
            try {
                console.log('🔄 Tentando listar usuários via RPC...');
                const { data: rpcData, error: rpcError } = await supabase.rpc('list_user_profiles');
                
                if (!rpcError && rpcData) {
                    console.log('✅ Usuários listados via RPC:', rpcData.length, 'usuários');
                    return rpcData;
                }
                
                console.warn('RPC list_user_profiles falhou, tentando consulta direta:', rpcError);
            } catch (rpcError) {
                console.warn('RPC list_user_profiles não disponível, tentando consulta direta:', rpcError);
            }

            // Fallback: consulta direta sem JOIN para evitar problemas de RLS
            console.log('🔄 Tentando consulta direta sem JOIN...');
            let query = supabase
                .from('user_profiles')
                .select('*');

            // Aplicar filtros se fornecidos
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    query = query.eq(key, value);
                }
            });

            const { data: usersData, error } = await query;
            if (error) {
                console.error('❌ Erro na consulta direta:', error);
                throw error;
            }
            
            console.log('✅ Usuários listados via consulta direta:', usersData?.length || 0, 'usuários');
            
            // Para cada usuário que tem company_id, buscar dados da empresa separadamente
            if (usersData && usersData.length > 0) {
                for (let user of usersData) {
                    if (user.company_id) {
                        try {
                            const { data: companyData, error: companyError } = await supabase
                                .from('companies')
                                .select('id, name, email, phone')
                                .eq('id', user.company_id)
                                .single();
                            
                            if (!companyError && companyData) {
                                user.companies = companyData;
                            } else {
                                user.companies = null;
                            }
                        } catch (companyError) {
                            console.warn('⚠️ Erro não crítico ao buscar empresa para usuário:', user.id, companyError);
                            user.companies = null;
                        }
                    } else {
                        user.companies = null;
                    }
                }
            }
            
            return usersData || [];
        } catch (error) {
            console.error('Error listing users:', error);
            throw error;
        }
    },

    // Login
    async signIn(email, password) {
        console.log('🔄 Iniciando processo de login...');
        
        // Usar diretamente a autenticação customizada (mais confiável)
        try {
            console.log('🔄 Tentando autenticação customizada via RPC...');
            const { data: customResult, error: rpcError } = await supabase.rpc('custom_auth_login', {
                login_email: email,
                login_password: password
            });
            
            console.log('📊 Resultado RPC:', { customResult, rpcError });
            
            if (rpcError) {
                console.error('❌ Erro RPC:', rpcError);
                throw rpcError;
            }
            
            if (!customResult || !customResult.success) {
                console.error('❌ Autenticação customizada falhou:', customResult);
                throw new Error(customResult?.error || 'Credenciais inválidas');
            }
            
            console.log('✅ Autenticação customizada bem-sucedida!');
            
            // Salvar sessão customizada
            const sessionData = {
                access_token: `custom_token_${Date.now()}`,
                refresh_token: `custom_refresh_${Date.now()}`,
                expires_in: 3600,
                token_type: 'bearer',
                user: customResult.user
            };
            
            this._setCustomSession(sessionData);
            
            // Simular estrutura de resposta do Supabase Auth
            return {
                user: customResult.user,
                session: sessionData
            };
        } catch (customError) {
            console.error('❌ Erro na autenticação customizada:', customError);
            
            // Fallback: tentar Supabase Auth como última opção
            try {
                console.log('🔄 Tentando Supabase Auth como fallback...');
                const { data, error } = await supabase.auth.signInWithPassword({
                    email,
                    password
                });
                if (error) throw error;
                console.log('✅ Supabase Auth funcionou como fallback!');
                return data;
            } catch (supabaseError) {
                console.error('❌ Supabase Auth também falhou:', supabaseError.message);
                throw customError; // Lançar o erro da autenticação customizada
            }
        }
    },

    // Registro
    async signUp(email, password, metadata = {}) {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: metadata
            }
        });
        if (error) throw error;
        return data;
    },

    // Logout
    async signOut() {
        try {
            const { error } = await supabase.auth.signOut();
            if (error && !error.message.includes('session_not_found')) {
                throw error;
            }
        } catch (error) {
            // Ignorar erros de sessão não encontrada
            console.warn('Erro no signOut do Supabase (ignorado):', error.message);
        }
        
        // Sempre limpar sessão customizada
        this._clearCustomSession();
        return true;
    },

    // Recuperar senha
    async resetPassword(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        return true;
    },

    // Atualizar senha
    async updatePassword(password) {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        return true;
    },

    // Escutar mudanças de auth
    onAuthStateChange(callback) {
        return supabase.auth.onAuthStateChange(callback);
    },

    // Filtrar apenas campos válidos da tabela user_profiles
    _filterValidUserFields(userData) {
        const validFields = [
            'id', 'user_id', 'email', 'name', 'phone', 'avatar_url', 'role', 'company_id', 
            'is_admin', 'preferences', 'created_at', 'updated_at', 'password', 'status', 'is_active'
        ];
        
        const filteredData = {};
        Object.keys(userData).forEach(key => {
            if (validFields.includes(key) && userData[key] !== undefined) {
                filteredData[key] = userData[key];
            }
        });
        
        return filteredData;
    },

    // Método create para criar usuários (usado em formulários)
    async create(userData) {
        try {
            // Filtrar apenas campos válidos
            const filteredData = this._filterValidUserFields(userData);
            console.log('📝 Dados filtrados para criação:', filteredData);
            
            // Tentar usar função RPC para criar usuário
            const { data, error } = await supabase.rpc('create_user_profile', {
                user_data: filteredData
            });
            
            if (error) {
                console.warn('RPC create_user_profile falhou, tentando inserção direta:', error);
                
                // Fallback: inserção direta na tabela user_profiles
                const { data: directResult, error: directError } = await supabase
                    .from('user_profiles')
                    .insert(filteredData)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro na inserção direta:', directError);
                    throw directError;
                }
                
                console.log('✅ Usuário criado via inserção direta:', directResult);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Usuário criado via RPC:', data);
            return data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    // Método update para atualizar usuários (usado em formulários)
    async update(userId, userData) {
        try {
            // Caso especial: se for apenas atualização de senha, usar RPC específica
            if (userData.password && Object.keys(userData).length === 1) {
                console.log('🔐 Atualizando senha via RPC específica...');
                const { data, error } = await supabase.rpc('update_user_password', {
                    user_uuid: userId,
                    new_password: userData.password
                });
                
                if (error) {
                    console.error('❌ Erro na RPC update_user_password:', error);
                    throw error;
                }
                
                if (!data.success) {
                    console.error('❌ RPC retornou erro:', data.error);
                    throw new Error(data.error);
                }
                
                console.log('✅ Senha atualizada via RPC:', data.message);
                return { id: userId, message: data.message };
            }
            // Se for update de is_active, forçar update direto
            if (Object.prototype.hasOwnProperty.call(userData, 'is_active')) {
                const filteredData = this._filterValidUserFields(userData);
                const { data: directResult, error: directError } = await supabase
                    .from('user_profiles')
                    .update(filteredData)
                    .eq('id', userId)
                    .select();
                if (directError) {
                    console.error('❌ Erro no update direto:', directError);
                    throw directError;
                }
                console.log('✅ Usuário atualizado via update direto (is_active):', directResult);
                return directResult[0] || directResult;
            }
            // Filtrar apenas campos válidos para atualizações normais
            const filteredData = this._filterValidUserFields(userData);
            console.log('📝 Dados filtrados para atualização:', filteredData);
            console.log('🖼️ Avatar URL sendo enviado:', filteredData.avatar_url);
            
            // Tentar usar função RPC para atualizar usuário
            const { data, error } = await supabase.rpc('update_user_profile', {
                p_user_id: userId,
                user_data: filteredData
            });
            
            if (error) {
                console.warn('RPC update_user_profile falhou, tentando update direto:', error);
                
                // Fallback: update direto na tabela user_profiles
                const { data: directResult, error: directError } = await supabase
                    .from('user_profiles')
                    .update(filteredData)
                    .eq('id', userId)
                    .select();
                
                if (directError) {
                    console.error('❌ Erro no update direto:', directError);
                    throw directError;
                }
                
                console.log('✅ Usuário atualizado via update direto:', directResult);
                console.log('🖼️ Avatar URL retornado (update direto):', directResult?.[0]?.avatar_url);
                return directResult[0] || directResult;
            }
            
            console.log('✅ Usuário atualizado via RPC:', data);
            console.log('🖼️ Avatar URL retornado (RPC):', data?.avatar_url);
            return data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    // Métodos de presença
    async updatePresence(userId = null, lastSeen = null) {
        try {
            console.log('💓 Atualizando presença do usuário:', { userId, lastSeen });
            
            const userIdToUpdate = userId || this._getCustomSession()?.user?.id;
            if (!userIdToUpdate) {
                throw new Error('ID do usuário não fornecido');
            }

            const timestamp = lastSeen || new Date().toISOString();

            // Tentar usar função RPC primeiro
            try {
                const { data, error } = await supabase.rpc('update_user_presence', {
                    p_user_id: userIdToUpdate,
                    p_last_seen: timestamp
                });

                if (error) throw error;
                
                console.log('✅ Presença atualizada via RPC:', data);
                return data;
                
            } catch (rpcError) {
                console.warn('⚠️ RPC falhou, tentando fallback:', rpcError.message);
                
                // Fallback - atualização direta
                const { data, error: updateError } = await supabase
                    .from('user_profiles')
                    .update({ 
                        last_seen: timestamp,
                        updated_at: timestamp
                    })
                    .eq('id', userIdToUpdate)
                    .select('id, last_seen, updated_at')
                    .single();

                if (updateError) throw updateError;
                
                console.log('✅ Presença atualizada via fallback:', data);
                return {
                    user_id: data.id,
                    last_seen: data.last_seen,
                    status: 'online',
                    updated_at: data.updated_at
                };
            }

        } catch (error) {
            console.error('❌ Erro ao atualizar presença:', error);
            throw new Error(`Erro ao atualizar presença: ${error.message}`);
        }
    },

    async getUsersOnlineStatus() {
        try {
            console.log('👥 Buscando status online dos usuários...');
            
            // Tentar usar função RPC primeiro
            try {
                const { data, error } = await supabase.rpc('get_users_online_status');
                
                if (error) throw error;
                
                console.log('✅ Status dos usuários obtido via RPC:', data?.length || 0, 'usuários');
                return data || [];
                
            } catch (rpcError) {
                console.warn('⚠️ RPC falhou, tentando fallback:', rpcError.message);
                
                // Fallback - consulta direta
                const { data, error: queryError } = await supabase
                    .from('user_profiles')
                    .select('id, name, email, avatar_url, role, last_seen, updated_at, created_at')
                    .not('email', 'is', null);

                if (queryError) throw queryError;
                
                // Processar status offline/online no cliente
                const now = new Date();
                const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
                
                const usersWithStatus = data.map(user => {
                    const lastSeen = new Date(user.last_seen || user.updated_at || user.created_at);
                    const isOnline = lastSeen > twoMinutesAgo;
                    
                    return {
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        avatar_url: user.avatar_url,
                        role: user.role,
                        last_seen: user.last_seen || user.updated_at,
                        status: isOnline ? 'online' : 'offline',
                        minutes_since_last_seen: Math.floor((now - lastSeen) / (1000 * 60))
                    };
                });
                
                console.log('✅ Status dos usuários processado via fallback:', usersWithStatus.length, 'usuários');
                return usersWithStatus;
            }

        } catch (error) {
            console.error('❌ Erro ao buscar status dos usuários:', error);
            return [];
        }
    },

    async getOnlineUsers() {
        try {
            console.log('🟢 Buscando usuários online...');
            
            // Tentar usar função RPC primeiro
            try {
                const { data, error } = await supabase.rpc('get_online_users');
                
                if (error) throw error;
                
                console.log('✅ Usuários online obtidos via RPC:', data?.length || 0, 'usuários');
                return data || [];
                
            } catch (rpcError) {
                console.warn('⚠️ RPC falhou, tentando fallback:', rpcError.message);
                
                // Fallback - consulta direta
                const now = new Date();
                const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
                
                const { data, error: queryError } = await supabase
                    .from('user_profiles')
                    .select('id, name, email, avatar_url, role, last_seen, updated_at, created_at')
                    .not('email', 'is', null)
                    .gte('last_seen', twoMinutesAgo.toISOString());

                if (queryError) throw queryError;
                
                const onlineUsers = data.map(user => ({
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    avatar_url: user.avatar_url,
                    role: user.role,
                    last_seen: user.last_seen || user.updated_at,
                    status: 'online'
                }));
                
                console.log('✅ Usuários online processados via fallback:', onlineUsers.length, 'usuários');
                return onlineUsers;
            }

        } catch (error) {
            console.error('❌ Erro ao buscar usuários online:', error);
            return [];
        }
    },

    // Alias para compatibilidade
    findAll: function() {
        return this.list.apply(this, arguments);
    }
};

// Entidade Module (SystemModule)
export const Module = {
    // Método findById para buscar módulo por ID
    async findById(moduleId) {
        try {
            console.log('🔍 Buscando módulo por ID:', moduleId);
            
            const { data, error } = await supabase
                .from('system_modules')
                .select('*')
                .eq('id', moduleId)
                .single();
            
            if (error) {
                if (error.code === 'PGRST116') {
                    console.log('ℹ️ Módulo não encontrado:', moduleId);
                    return null;
                }
                throw error;
            }
            
            console.log('✅ Módulo encontrado:', data?.name);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar módulo:', error);
            throw error;
        }
    },

    // Método list para listar módulos
    async list(filters = {}) {
        try {
            console.log('🔄 Listando módulos...');
            
            let query = supabase
                .from('system_modules')
                .select('*')
                .order('name', { ascending: true });
            
            // Aplicar filtros se fornecidos
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    query = query.eq(key, value);
                }
            });
            
            const { data, error } = await query;
            
            if (error) throw error;
            console.log('✅ Módulos listados:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('❌ Erro ao listar módulos:', error);
            throw error;
        }
    },

    // Método create
    async create(moduleData) {
        try {
            console.log('🔄 Criando módulo...');
            
            const { data, error } = await supabase
                .from('system_modules')
                .insert(moduleData)
                .select()
                .single();
            
            if (error) throw error;
            console.log('✅ Módulo criado:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao criar módulo:', error);
            throw error;
        }
    },

    // Método update
    async update(moduleId, updateData) {
        try {
            console.log('🔄 Atualizando módulo...');
            
            const { data, error } = await supabase
                .from('system_modules')
                .update({
                    ...updateData,
                    updated_at: new Date().toISOString()
                })
                .eq('id', moduleId)
                .select()
                .single();
            
            if (error) throw error;
            console.log('✅ Módulo atualizado:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao atualizar módulo:', error);
            throw error;
        }
    },

    // Método delete
    async delete(moduleId) {
        try {
            console.log('🗑️ Deletando módulo...');
            
            const { error } = await supabase
                .from('system_modules')
                .delete()
                .eq('id', moduleId);
            
            if (error) throw error;
            console.log('✅ Módulo deletado');
            return { success: true };
        } catch (error) {
            console.error('❌ Erro ao deletar módulo:', error);
            throw error;
        }
    }
};

// Funções auxiliares específicas
export const Dashboard = {
    async getStats(companyId) {
        const { data, error } = await supabase.rpc('get_dashboard_stats', {
            company_id_param: companyId
        });
        if (error) throw error;
        return data;
    }
};

export const Search = {
    async knowledgeBase(query, companyId = null) {
        const { data, error } = await supabase.rpc('search_knowledge_base', {
            search_query: query,
            company_id_param: companyId
        });
        if (error) throw error;
        return data;
    }
};

// Objeto Reports com funções RPC avançadas para relatórios
export const Reports = {
    // Estatísticas gerais de tickets por período
    async getTicketsStatsByPeriod(startDate = null, endDate = null, companyId = null) {
        try {
            console.log('📊 Buscando estatísticas de tickets por período...');
            const { data, error } = await supabase.rpc('get_tickets_stats_by_period', {
                start_date_param: startDate,
                end_date_param: endDate,
                company_id_param: companyId
            });
            
            if (error) throw error;
            console.log('✅ Estatísticas carregadas:', data);
            return data[0] || {
                total_tickets: 0,
                new_tickets: 0,
                resolved_tickets: 0,
                closed_tickets: 0,
                avg_resolution_hours: 0,
                sla_compliance_percentage: 0
            };
        } catch (error) {
            console.error('❌ Erro ao buscar estatísticas de tickets:', error);
            
            // Fallback: calcular estatísticas localmente
            try {
                console.log('🔄 Fallback: calculando estatísticas de tickets localmente...');
                
                // Construir query base
                let query = supabase
                    .from('tickets')
                    .select('status, created_at, resolved_at, due_date, company_id')
                    .gte('created_at', startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
                    .lte('created_at', endDate || new Date().toISOString());
                
                // Aplicar filtro de company_id apenas se fornecido
                if (companyId) {
                    query = query.eq('company_id', companyId);
                }
                
                const { data: tickets, error: ticketsError } = await query;
                
                if (ticketsError) throw ticketsError;
                
                const stats = {
                    total_tickets: tickets?.length || 0,
                    new_tickets: 0,
                    resolved_tickets: 0,
                    closed_tickets: 0,
                    total_resolution_hours: 0,
                    resolution_count: 0,
                    sla_compliant: 0,
                    sla_total: 0
                };
                
                tickets?.forEach(ticket => {
                    // Contar por status
                    if (['new', 'open'].includes(ticket.status)) {
                        stats.new_tickets++;
                    } else if (ticket.status === 'resolved') {
                        stats.resolved_tickets++;
                    } else if (ticket.status === 'closed') {
                        stats.closed_tickets++;
                    }
                    
                    // Calcular tempo de resolução
                    if (ticket.resolved_at && ticket.created_at) {
                        const created = new Date(ticket.created_at);
                        const resolved = new Date(ticket.resolved_at);
                        const hours = (resolved - created) / (1000 * 60 * 60);
                        
                        stats.total_resolution_hours += hours;
                        stats.resolution_count++;
                    }
                    
                    // Calcular SLA (apenas para tickets com due_date)
                    if (ticket.due_date) {
                        stats.sla_total++;
                        
                        if (ticket.resolved_at) {
                            const dueDate = new Date(ticket.due_date);
                            const resolvedDate = new Date(ticket.resolved_at);
                            
                            if (resolvedDate <= dueDate) {
                                stats.sla_compliant++;
                            }
                        }
                    }
                });
                
                const result = {
                    total_tickets: stats.total_tickets,
                    new_tickets: stats.new_tickets,
                    resolved_tickets: stats.resolved_tickets,
                    closed_tickets: stats.closed_tickets,
                    avg_resolution_hours: stats.resolution_count > 0 
                        ? Math.round((stats.total_resolution_hours / stats.resolution_count) * 100) / 100 
                        : 0,
                    sla_compliance_percentage: stats.sla_total > 0 
                        ? Math.round((stats.sla_compliant / stats.sla_total) * 100 * 100) / 100 
                        : 0
                };
                
                console.log('✅ Estatísticas de tickets calculadas localmente:', result);
                return result;
            } catch (fallbackError) {
                console.error('❌ Erro no fallback de estatísticas:', fallbackError);
                return {
                    total_tickets: 0,
                    new_tickets: 0,
                    resolved_tickets: 0,
                    closed_tickets: 0,
                    avg_resolution_hours: 0,
                    sla_compliance_percentage: 0
                };
            }
        }
    },

    // Performance de usuários
    async getUserPerformanceStats(startDate = null, endDate = null) {
        try {
            console.log('👥 Buscando estatísticas de performance dos usuários...');
            const { data, error } = await supabase.rpc('get_user_performance_stats', {
                start_date_param: startDate,
                end_date_param: endDate
            });
            
            if (error) throw error;
            console.log('✅ Performance de usuários carregada:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('❌ Erro ao buscar performance de usuários:', error);
            
            // Fallback: calcular performance localmente
            try {
                console.log('🔄 Fallback: calculando performance de usuários localmente...');
                
                // Buscar usuários internos
                const { data: users, error: usersError } = await supabase
                    .from('user_profiles')
                    .select('id, name, email, role')
                    .in('role', ['admin', 'support', 'developer', 'manager']);
                
                if (usersError) throw usersError;
                
                // Buscar tickets no período
                const { data: tickets, error: ticketsError } = await supabase
                    .from('tickets')
                    .select('assigned_to, status, created_at, resolved_at')
                    .gte('created_at', startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
                    .lte('created_at', endDate || new Date().toISOString())
                    .not('assigned_to', 'is', null);
                
                if (ticketsError) throw ticketsError;
                
                // Calcular estatísticas por usuário
                const userStats = {};
                
                users?.forEach(user => {
                    userStats[user.id] = {
                        user_id: user.id,
                        user_name: user.name || 'Usuário',
                        user_email: user.email,
                        total_tickets: 0,
                        resolved_tickets: 0,
                        total_resolution_hours: 0,
                        resolution_count: 0
                    };
                });
                
                tickets?.forEach(ticket => {
                    if (userStats[ticket.assigned_to]) {
                        userStats[ticket.assigned_to].total_tickets++;
                        
                        if (ticket.status === 'resolved' && ticket.resolved_at) {
                            userStats[ticket.assigned_to].resolved_tickets++;
                            
                            // Calcular tempo de resolução
                            const created = new Date(ticket.created_at);
                            const resolved = new Date(ticket.resolved_at);
                            const hours = (resolved - created) / (1000 * 60 * 60);
                            
                            userStats[ticket.assigned_to].total_resolution_hours += hours;
                            userStats[ticket.assigned_to].resolution_count++;
                        }
                    }
                });
                
                // Converter para formato esperado
                const result = Object.values(userStats)
                    .filter(stats => stats.total_tickets > 0)
                    .map(stats => ({
                        user_id: stats.user_id,
                        user_name: stats.user_name,
                        user_email: stats.user_email,
                        total_tickets: stats.total_tickets,
                        resolved_tickets: stats.resolved_tickets,
                        avg_resolution_hours: stats.resolution_count > 0 
                            ? Math.round((stats.total_resolution_hours / stats.resolution_count) * 100) / 100 
                            : 0,
                        resolution_rate: stats.total_tickets > 0 
                            ? Math.round((stats.resolved_tickets / stats.total_tickets) * 100 * 100) / 100 
                            : 0
                    }))
                    .sort((a, b) => b.total_tickets - a.total_tickets || b.resolution_rate - a.resolution_rate);
                
                console.log('✅ Performance de usuários calculada localmente:', result.length);
                return result;
            } catch (fallbackError) {
                console.error('❌ Erro no fallback de performance:', fallbackError);
                return [];
            }
        }
    }
};

// Entidade Client
export const Client = {
    // Método create usando consulta direta
    async create(clientData) {
        try {
            console.log('🔄 Criando cliente via inserção direta...');
            
            const { data, error } = await supabase
                .from('clients')
                .insert(clientData)
                .select()
                .single();
            
            if (error) throw error;
            console.log('✅ Cliente criado:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao criar cliente:', error);
            throw error;
        }
    },

    // Método list usando consulta direta
    async list(filters = {}) {
        try {
            console.log('🔄 Listando clientes via consulta direta...');
            
            let query = supabase.from('clients').select('*');
            
            // Aplicar filtros se fornecidos
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (key === 'search') {
                        // Busca em múltiplos campos
                        query = query.or(`name.ilike.%${value}%,company_name.ilike.%${value}%,corporate_name.ilike.%${value}%,email.ilike.%${value}%,cnpj.ilike.%${value}%`);
                    } else {
                        query = query.eq(key, value);
                    }
                }
            });
            
            // Ordenar por nome
            query = query.order('name', { ascending: true });
            
            const { data, error } = await query;
            
            if (error) throw error;
            console.log('✅ Clientes listados:', data?.length || 0);
            return data || [];
        } catch (error) {
            console.error('❌ Erro ao listar clientes:', error);
            throw error;
        }
    },

    // Método findById usando RPC get_client_by_id
    async findById(clientId) {
        try {
            console.log('🔍 Buscando cliente por ID via RPC get_client_by_id:', clientId);
            
            const { data, error } = await supabase.rpc('get_client_by_id', {
                p_client_id: clientId
            });
            
            if (error) {
                console.error('❌ Erro na RPC get_client_by_id:', error);
                throw error;
            }
            
            if (!data) {
                console.warn(`⚠️ Cliente com ID ${clientId} não encontrado`);
                return null;
            }
            
            console.log('✅ Cliente encontrado via RPC:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao buscar cliente por ID:', error);
            throw error;
        }
    },

    // Método update
    async update(clientId, updateData) {
        try {
            console.log('🔄 Atualizando cliente via atualização direta...');
            
            const { data, error } = await supabase
                .from('clients')
                .update({
                    ...updateData,
                    updated_at: new Date().toISOString()
                })
                .eq('id', clientId)
                .select()
                .single();
            
            if (error) throw error;
            console.log('✅ Cliente atualizado:', data);
            return data;
        } catch (error) {
            console.error('❌ Erro ao atualizar cliente:', error);
            throw error;
        }
    },

    // Método delete
    async delete(clientId) {
        try {
            console.log('🗑️ Deletando cliente via deleção direta...');
            
            const { error } = await supabase
                .from('clients')
                .delete()
                .eq('id', clientId);
            
            if (error) throw error;
            console.log('✅ Cliente deletado');
            return { success: true };
        } catch (error) {
            console.error('❌ Erro ao deletar cliente:', error);
            throw error;
        }
    },

    // Alias para compatibilidade
    findAll: function() {
        return this.list.apply(this, arguments);
    }
};